import React, { useEffect } from 'react';

import { FieldErrors, UseFormClearErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';

import { SIP_TRUNK_REASON_OPTIONS } from '../constants';
import { SipTrunkReason } from '../types';

export type SipTrunkFormValues = {
  reason: SipTrunkReason;
  reasonOther: string;
  monthlyCallVolume: string;
  dailyCallVolume: string;
  peakConcurrentCalls: string;
  softphoneUsers: string;
  locations: string;
  didNumbersNeeded: string;
  portingRequired: boolean;
  portingDetails: string;
  callRecordingRequired: boolean;
  expectedGoLive: string;
};

type SipTrunkFieldsProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clearErrors: UseFormClearErrors<any>;
};

const VOLUME_FIELDS = ['monthlyCallVolume', 'dailyCallVolume'];
const VOLUME_REQUIRED_MESSAGE = 'Give the expected volume per month or per day.';

const SipTrunkFields: React.FC<SipTrunkFieldsProps> = ({
  register,
  errors,
  watch,
  clearErrors,
}: SipTrunkFieldsProps) => {
  const reason = watch('reason') as SipTrunkReason;
  const portingRequired = watch('portingRequired') as boolean;
  const monthlyCallVolume = watch('monthlyCallVolume') as string;
  const dailyCallVolume = watch('dailyCallVolume') as string;

  // The backend accepts either figure; requesters know their client's traffic
  // in whichever unit the client quoted it.
  const hasVolume = !!monthlyCallVolume?.trim() || !!dailyCallVolume?.trim();

  // This rule spans two inputs, and react-hook-form 7.20 has no `deps` option
  // (added in 7.29). Without this, filling the sibling field satisfies the rule
  // but leaves the error frozen on screen.
  useEffect(() => {
    if (hasVolume) clearErrors(VOLUME_FIELDS);
  }, [hasVolume, clearErrors]);

  return (
    <fieldset className="mt-5">
      <legend className="form-legend">SIP trunk</legend>

      <label className="block mt-3 text-sm">
        Why can&apos;t we use the client&apos;s own trunk?
        <select className="input w-full" {...register('reason')}>
          {SIP_TRUNK_REASON_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {reason === 'OTHER' && (
        <label className="block mt-3 text-sm">
          Explain
          <textarea
            className="input w-full"
            rows={2}
            {...register('reasonOther', {
              validate: (value: string) => !!value?.trim() || 'Explain the reason.',
            })}
          />
          {errors.reasonOther && (
            <span className="text-xs text-red-600">{String(errors.reasonOther.message)}</span>
          )}
        </label>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3">
        <label className="block text-sm">
          Calls per month
          <input
            type="number"
            min="0"
            className="input w-full"
            {...register('monthlyCallVolume', {
              validate: () => hasVolume || VOLUME_REQUIRED_MESSAGE,
            })}
          />
        </label>
        <label className="block text-sm">
          Calls per day
          <input
            type="number"
            min="0"
            className="input w-full"
            {...register('dailyCallVolume', {
              validate: () => hasVolume || VOLUME_REQUIRED_MESSAGE,
            })}
          />
        </label>
      </div>
      {!hasVolume && (errors.monthlyCallVolume || errors.dailyCallVolume) && (
        <span className="text-xs text-red-600">{VOLUME_REQUIRED_MESSAGE}</span>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3">
        <label className="block text-sm">
          Peak concurrent calls
          <input
            type="number"
            min="1"
            className="input w-full"
            {...register('peakConcurrentCalls', { required: 'Required — this sizes the trunk.' })}
          />
          {errors.peakConcurrentCalls && (
            <span className="text-xs text-red-600">
              {String(errors.peakConcurrentCalls.message)}
            </span>
          )}
        </label>
        <label className="block text-sm">
          Softphone users
          <input
            type="number"
            min="1"
            className="input w-full"
            {...register('softphoneUsers', { required: 'Required.' })}
          />
          {errors.softphoneUsers && (
            <span className="text-xs text-red-600">{String(errors.softphoneUsers.message)}</span>
          )}
        </label>
        <label className="block text-sm">
          Locations sharing the trunk
          <input type="number" min="1" className="input w-full" {...register('locations')} />
        </label>
        <label className="block text-sm">
          New DIDs needed
          <input type="number" min="0" className="input w-full" {...register('didNumbersNeeded')} />
        </label>
        <label className="block text-sm">
          Expected go-live
          <input type="date" className="input w-full" {...register('expectedGoLive')} />
        </label>
      </div>

      <label className="flex items-center gap-2 mt-3 text-sm">
        <input type="checkbox" {...register('portingRequired')} />
        Client is porting existing numbers
      </label>

      {portingRequired && (
        <label className="block mt-3 text-sm">
          Numbers to port and current carrier
          <textarea
            className="input w-full"
            rows={2}
            {...register('portingDetails', {
              validate: (value: string) =>
                !!value?.trim() || 'List the numbers and their current carrier.',
            })}
          />
          {errors.portingDetails && (
            <span className="text-xs text-red-600">{String(errors.portingDetails.message)}</span>
          )}
        </label>
      )}

      <label className="flex items-center gap-2 mt-3 text-sm">
        <input type="checkbox" {...register('callRecordingRequired')} />
        Call recording required
      </label>
    </fieldset>
  );
};

export default SipTrunkFields;
